import { Link } from "react-router-dom";
import { TownEyeLogo } from "@/components/TownEyeLogo";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  MapPin,
  Users,
  Bell,
  Camera,
  ShieldCheck,
  Heart,
  MessageSquare,
  Sparkles,
  Globe2,
  ChevronRight,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1493794179168-82ca7cb00437?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxjaXR5JTIwc3RyZWV0JTIwcGVvcGxlJTIwY29tbXVuaXR5fGVufDB8fHx8MTc4MTU0MTY4NHww&ixlib=rb-4.1.0&q=85";

const FEATURE_IMG =
  "https://images.unsplash.com/photo-1548345680-f5475ea5df84?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtYXAlMjBuYXZpZ2F0aW9uJTIwc21hcnRwaG9uZXxlbnwwfHx8fDE3ODE1NDE3MDB8MA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Camera, title: "Report with proof", body: "Snap a photo, drop a pin, describe the issue. Your neighborhood sees it instantly." },
  { icon: MapPin, title: "Live community map", body: "Track open reports near you on an interactive OpenStreetMap with categories." },
  { icon: Users, title: "Community feed", body: "Post local updates, share recommendations, organize block events." },
  { icon: Bell, title: "Smart notifications", body: "Get notified when someone reacts, comments, or solves a report you care about." },
  { icon: ShieldCheck, title: "Admin moderation", body: "Local moderators keep the conversation respectful and the data accurate." },
  { icon: Heart, title: "Likes & comments", body: "Show support, share context, and build momentum around what matters." },
];

const STATS = [
  { value: "12k+", label: "Reports resolved" },
  { value: "320", label: "Active towns" },
  { value: "98%", label: "Reply within 24h" },
  { value: "4.9★", label: "Community rating" },
];

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen" style={{ background: "var(--te-bg)" }}>
      {/* Nav */}
      <header className="te-glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="landing-logo">
            <TownEyeLogo />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[var(--te-text-secondary)]">
            <a href="#features" className="hover:text-[var(--te-text)]">Features</a>
            <a href="#how" className="hover:text-[var(--te-text)]">How it works</a>
            <a href="#community" className="hover:text-[var(--te-text)]">Community</a>
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/dashboard" data-testid="landing-go-dashboard-btn" className="te-btn-primary text-sm py-2 px-4">
                Open app
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-login-btn" className="hidden sm:inline-block te-btn-secondary text-sm py-2 px-4">
                  Sign in
                </Link>
                <Link to="/register" data-testid="landing-signup-btn" className="te-btn-primary text-sm py-2 px-4 flex items-center gap-1">
                  Join TownEye <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="te-eyebrow mb-6 inline-flex items-center gap-2">
              <Sparkles size={14} /> Now on the web
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[var(--te-text)]">
              Your neighborhood,<br />
              <span style={{ color: "var(--te-primary)" }}>seen and heard.</span>
            </h1>
            <p className="mt-6 text-lg text-[var(--te-text-secondary)] max-w-xl leading-relaxed">
              TownEye is the civic platform where residents report issues, share local updates, and team up
              with neighbors and city services — all on a beautiful, mobile-first map.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register" data-testid="hero-cta-signup" className="te-btn-primary inline-flex items-center justify-center gap-2">
                Start reporting free <ArrowRight size={16} />
              </Link>
              <Link to="/login" data-testid="hero-cta-login" className="te-btn-secondary inline-flex items-center justify-center gap-2">
                I have an account
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-xl">
              {STATS.map((s) => (
                <div key={s.label}>
                  <div className="font-heading text-2xl font-bold text-[var(--te-text)]">{s.value}</div>
                  <div className="text-xs uppercase tracking-wider text-[var(--te-text-muted)] mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-[var(--te-divider)] shadow-xl">
              <img src={HERO_IMG} alt="Community street" className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--te-text)]/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 border border-[var(--te-divider)]">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#DC2626] flex items-center justify-center text-white">
                    <MapPin size={18} />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-[var(--te-text)]">Pothole on Oak St.</div>
                    <div className="text-xs text-[var(--te-text-muted)]">Reported 8m ago · 24 neighbors notified</div>
                  </div>
                  <span className="te-badge te-badge-progress">In Progress</span>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden lg:block bg-white border border-[var(--te-divider)] rounded-2xl p-4 shadow-md max-w-[220px]">
              <div className="flex items-center gap-2 text-[var(--te-primary)]">
                <Heart size={16} fill="currentColor" />
                <span className="text-sm font-semibold">+312</span>
              </div>
              <p className="text-xs text-[var(--te-text-secondary)] mt-1">Reactions on community posts this week</p>
            </div>
          </div>
        </div>
        <div className="te-divider-grain h-px max-w-7xl mx-auto" />
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <div className="te-eyebrow mb-4">What you get</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[var(--te-text)]">
            Everything your town needs to listen, decide, and act.
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="te-card te-card-hover p-6">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "var(--te-muted)" }}>
                <f.icon size={20} className="text-[var(--te-primary)]" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-[var(--te-text)] mb-2">{f.title}</h3>
              <p className="text-sm text-[var(--te-text-secondary)] leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="rounded-3xl overflow-hidden border border-[var(--te-divider)] shadow-xl">
            <img src={FEATURE_IMG} alt="Mobile map" className="w-full h-[520px] object-cover" />
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <div className="te-eyebrow mb-4">How it works</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[var(--te-text)]">
            Three steps from frustration to fix.
          </h2>
          <ol className="mt-10 space-y-6">
            {[
              { n: "01", t: "Spot something", b: "Pothole, lost cat, broken streetlight, a great new café — anything that matters to your block." },
              { n: "02", t: "Drop a pin", b: "Add a photo and short description. We auto-share to nearby neighbors and your town's moderators." },
              { n: "03", t: "Track to resolved", b: "Updates, comments, status changes — everyone follows along until the report is closed." },
            ].map((step) => (
              <li key={step.n} className="flex gap-5">
                <div className="font-heading text-3xl font-bold text-[#DC2626] w-16 shrink-0">{step.n}</div>
                <div>
                  <h4 className="font-heading text-xl font-semibold text-[var(--te-text)]">{step.t}</h4>
                  <p className="text-[var(--te-text-secondary)] mt-1 leading-relaxed">{step.b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Community CTA */}
      <section id="community" className="max-w-7xl mx-auto px-4 md:px-8 pb-24">
        <div className="relative rounded-3xl overflow-hidden border border-[var(--te-divider)] p-10 md:p-16" style={{ background: "var(--te-primary)" }}>
          <div className="absolute inset-0 opacity-10 te-divider-grain pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <Globe2 className="text-white/70 mb-4" size={32} />
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
                Plant the first pin on your town's map.
              </h2>
              <p className="mt-4 text-white/80 max-w-md leading-relaxed">
                Join thousands of residents and local officials making neighborhoods better, one report at a time.
              </p>
            </div>
            <div className="flex md:justify-end">
              <Link
                to="/register"
                data-testid="footer-cta-signup"
                className="inline-flex items-center gap-2 bg-[#DC2626] hover:bg-[#DC2626] text-white px-7 py-4 rounded-full font-medium text-lg transition-colors"
              >
                Get started — it's free <ChevronRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[var(--te-divider)] py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <TownEyeLogo size={22} />
          <div className="text-sm text-[var(--te-text-muted)]">© {new Date().getFullYear()} TownEye · towneye.app</div>
          <div className="flex items-center gap-5 text-sm text-[var(--te-text-secondary)]">
            <a href="#features" className="hover:text-[var(--te-text)]">Features</a>
            <a href="#how" className="hover:text-[var(--te-text)]">How it works</a>
            <Link to="/terms" className="hover:text-[var(--te-text)]" data-testid="footer-terms-link">Terms</Link>
            <Link to="/privacy" className="hover:text-[var(--te-text)]" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/login" className="hover:text-[var(--te-text)]">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
